import { supabase } from '@/integrations/supabase/client';
import { safeApiCall } from '.';

export const subscriptionsAPI = {
  // ✅ NEW: Single optimized call to get ALL subscription data at once
  async getCompleteSubscriptionData() {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Get ALL subscription data in one call from the view
        const { data, error } = await supabase
          .from('subscription_status_view')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (error) {
          
          throw error;
        }
        
        
        
        // Calculate trial days remaining if needed
        let trialDaysRemaining = 0;
        if (data.trial_active && data.ends_at) {
          const now = new Date();
          const trialEnd = new Date(data.ends_at);
          if (now < trialEnd) {
            const diffInMs = trialEnd.getTime() - now.getTime();
            trialDaysRemaining = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
          }
        }
        
        return {
          ...data,
          trialDaysRemaining
        };
      },
      {
        timeoutMs: 3000, // Single fast call instead of multiple 4s calls
        name: "getCompleteSubscriptionData",
        critical: true
      }
    );
  },

  // Get the current user's subscription information
  async getUserSubscription() {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Get subscription info from the subscriptions table
        const { data, error } = await supabase
          .from('subscription_status_view')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (error) {
          
          throw error;
        }
        
        return data;
      },
      {
        timeoutMs: 5000,
        name: "getUserSubscription",
        critical: true
      }
    );
  },
  
  // Get detailed subscription status from the view
  async getSubscriptionStatusDetails() {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Get subscription details from the subscription_status_view
        const { data, error } = await supabase
          .from('subscription_status_view')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (error) {
          
          throw error;
        }
        
        
        return data;
      },
      {
        timeoutMs: 5000,
        name: "getSubscriptionStatusDetails",
        critical: false
      }
    );
  },
  
  // Check if user can create a new project based on their subscription limits
  async canCreateProject() {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Get subscription data
        const { data, error } = await supabase
          .from('subscription_status_view')
          .select('plan, active_project_limit, active_project_count, trial_active, projects_locked, has_access')
          .eq('user_id', user.id)
          .single();

        
        if (error) {
          
          throw error;
        }
        
        // Check if user has access based on the view
        if (data.has_access === false) {
          return {
            canCreate: false,
            reason: 'NO_ACCESS'
          };
        }
        
        // If projects are locked, user can't create projects
        if (data.projects_locked) {
          return {
            canCreate: false,
            reason: 'PROJECTS_LOCKED'
          };
        }
        
        // Check if trial is active
        if (data.plan === 'trial' && data.trial_active) {
          // Check project limit for trials
          if (data.active_project_count >= data.active_project_limit) {
            return {
              canCreate: false,
              reason: 'TRIAL_LIMIT_REACHED',
              limit: data.active_project_limit
            };
          }
          return {
            canCreate: true,
            limit: data.active_project_limit,
            current: data.active_project_count
          };
        }
        
        // Check if subscription has expired
       
        
        
        // Check project limit for regular subscriptions
        if (data.active_project_count >= data.active_project_limit) {
          return {
            canCreate: false,
            reason: 'LIMIT_REACHED',
            limit: data.active_project_limit
          };
        }
        
        return {
          canCreate: true,
          limit: data.active_project_limit,
          current: data.active_project_count
        };
      },
      {
        timeoutMs: 5000,
        name: "canCreateProject",
        critical: true
      }
    );
  },
  
  // Get remaining trial days
  async getTrialDaysRemaining() {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Get subscription data
        const { data, error } = await supabase
          .from('subscription_status_view')
          .select('trial_active, ends_at')
          .eq('user_id', user.id)
          .single();
        
        if (error) {
          
          throw error;
        }
        
        // If trial is not active, return 0
        if (!data.trial_active) {
          return 0;
        }
        
        // Calculate days remaining
        const now = new Date();
        const trialEnd = new Date(data.ends_at || '');
        
        // If trial already ended, return 0
        if (now > trialEnd) {
          return 0;
        }
        
        // Calculate the remaining days
        const diffInMs = trialEnd.getTime() - now.getTime();
        const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
        
        return diffInDays;
      },
      {
        timeoutMs: 5000,
        name: "getTrialDaysRemaining",
        critical: false
      }
    );
  },
  
  // Update project count when a new project is created
  async incrementProjectCount() {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Update the active project count
        const { data, error } = await supabase
          .from('subscriptions')
          .update({
            active_project_count: supabase.rpc('increment_counter', { row_id: user.id, counter_name: 'active_project_count' }),
            updated_at: new Date()
          })
          .eq('user_id', user.id)
          .select();
        
        if (error) {
          
          throw error;
        }
        
        return data;
      },
      {
        timeoutMs: 5000,
        name: "incrementProjectCount",
        critical: true
      }
    );
  },
  
  // Update project count when a project is completed
  async decrementActiveProjectCount() {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Update the active and passive project counts
        const { data, error } = await supabase
          .from('subscriptions')
          .update({
            active_project_count: supabase.rpc('decrement_counter', { row_id: user.id, counter_name: 'active_project_count' }),
            passive_project_count: supabase.rpc('increment_counter', { row_id: user.id, counter_name: 'passive_project_count' }),
            updated_at: new Date()
          })
          .eq('user_id', user.id)
          .select();
        
        if (error) {
          
          throw error;
        }
        
        return data;
      },
      {
        timeoutMs: 5000,
        name: "decrementActiveProjectCount",
        critical: false
      }
    );
  },
  
  // Force refresh subscription data by checking the actual subscriptions table
  async getSubscriptionFromTable() {
    return safeApiCall(
      async () => {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Get subscription directly from the subscriptions table (not the view)
        const { data, error } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .single();
        
        if (error) {
          
          throw error;
        }
        
        return data;
      },
      {
        timeoutMs: 5000,
        name: "getSubscriptionFromTable",
        critical: false
      }
    );
  },

  // has_access is computed in the view — there is no column to update.
  // Kept for back-compat: callers now bump the user's tier directly.
  async forceUpdateHasAccess(hasAccess: boolean) {
    return safeApiCall(
      async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data, error } = await supabase
          .from('subscriptions')
          .update({
            tier: hasAccess ? 'premium' : 'free',
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      },
      {
        timeoutMs: 5000,
        name: "forceUpdateHasAccess",
        critical: false,
      }
    );
  },
}; 